import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState, useRef, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import StatusCell from '../SepTable/StatusCell';
import UserSearchBar from './AutoComplete';
import { ReadableTaskPhase, TaskExtended } from '../../../shared/types/Task';
import UserDisplay from '../UserDisplay/UserDisplay';
import { useGetSepQuery } from '../../services/sepSlice';
import { format, formatDistance } from 'date-fns';
import { User, UserShort } from '../../../shared/types/User';
import { useAccount } from '@azure/msal-react';
import { useGetDepartmentsQuery } from '../../services/departmentSlice/departmentSlice';
import { Department } from '../../../shared/types/Department';
import { env } from '../../../shared/env';
import { defaultSystemTask } from '../../../shared/types/Task';
import { getEscalationDate } from '../../utils/EscalationDatetaskUtils';
import { useGetTaskTemplatesQuery } from '../../services/tasksSlice/tasksSlice';
import getTaskAge from '../../../frontend/utils/taskAgeCalculation';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const IconCell = ({
  icon,
  iconHover,
  onClick,
}: {
  icon: string;
  iconHover: string;
  onClick: () => void;
}) => {
  const [hover, setHover] = useState(false);

  return (
    <Button
      sx={{
        minWidth: '20px',
        height: '20px',
        padding: '5px',
      }}
      onClick={onClick}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <i
        className={hover ? iconHover : icon}
        style={{
          fontSize: typographyTokens.fontSizes.xxs,
          color: hover ? '#2372B9' : '#7E8083',
        }}
      ></i>
    </Button>
  );
};

const StyledTableCell = styled(TableCell)({
  borderBottom: 'none',
  padding: '2px 30px 2px 8px',
});

interface TaskHeaderProps {
  task: TaskExtended;
  onChange: (changedTask: TaskExtended) => void;
}

const TaskHeader = ({ task, onChange }: TaskHeaderProps) => {
  const theme = useTheme();
  const { data: sep } = useGetSepQuery(Number(task.sepID));
  const [isAssignerEdit, setIsAssignerEdit] = useState(false);
  const [isReviewerEdit, setIsReviewerEdit] = useState(false);
  const assigneeRef = useRef(null);
  const reviewerRef = useRef(null);
  const timeZoneAbbrev = new Date()
    .toLocaleTimeString('en-us', { timeZoneName: 'short' })
    .split(' ')[2];

  const handleChangeUser = (type: string, user: User) => {
    if (type === 'assignee') {
      onChange({
        ...task,
        assignedUserID: user.id,
        assignee: user as UserShort,
      });
      setIsAssignerEdit(false);
    } else {
      onChange({
        ...task,
        defaultReviewerID: user.id,
        defaultReviewer: user as UserShort,
      });
      setIsReviewerEdit(false);
    }
  };
  const isDarkMode = theme.palette.mode === 'dark';
  const { data: taskTemplates } = useGetTaskTemplatesQuery();
  const account = useAccount();
  const { data: departments } = useGetDepartmentsQuery();

  const isDeptStakeholder = useMemo(() => {
    const deptADAppRole = departments?.find(
      (d: Department) => d.id === task.departmentID
    )?.adAppRole;
    if (env.REACT_APP_LOCAL_DEV_ADMIN === 'true') {
      return ['AuthSuperUser'];
    }
    return (
      account?.idTokenClaims?.roles?.includes('AuthSuperUser') ||
      (deptADAppRole && account?.idTokenClaims?.roles?.includes(deptADAppRole))
    );
  }, [account, departments, task.departmentID]);

  const {activeDays, color, ageTextColor, userAssigmentDetails } = getTaskAge(task, taskTemplates, theme);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Grid item sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between' }}>
        <Typography
          aria-label="SEP ID"
          fontSize={typographyTokens.fontSizes.xl}
        >
          <a href={`/sep/${task.sepID}`}>
            SEP #{task.sepID}:
          </a>
          <span>{` ${sep?.name || ''}`}</span>
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
          <Typography
            aria-label="task last updated"
            fontSize={typographyTokens.fontSizes.xxs}
            color="gray.darkGray1"
          >
            Last edited: {format(new Date(task.updatedAt), 'M/d/yy h:mma')}{' '}
            {timeZoneAbbrev}
          </Typography>
        </Box>
      </Grid>
      <Typography
        aria-label="title"
        fontSize={typographyTokens.fontSizes.h5}
        fontWeight={typographyTokens.fontWeights.semibold}
        color={isDarkMode ? theme.palette.white.main : "gray.main"}
        mb="16px"
      >
        {task.name}
      </Typography>
      {task?.completedAt &&
        (task?.status === "pending" || task?.status === "todo") && (
          <Typography
            aria-label="title"
            fontSize={typographyTokens.fontSizes.h5}
            fontWeight={typographyTokens.fontWeights.semibold}
            mb="16px"
            bgcolor={theme.palette.error.light}
          >
            {`This task was completed on ${format(
              new Date(task.completedAt),
              "M/d/yy h:mma"
            )}${
              timeZoneAbbrev ? ` ${timeZoneAbbrev}` : ""
            } but has been reverted to Pending / ToDo status as dependencies were added.`}
          </Typography>
        )}
      <StatusCell status={task.status} />
      <Box mt={4}>
        <Grid container spacing={2} sx={{ width: { xs: '100%' }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
          {/* Assigned to */}
          <Grid item xs={12} md={4} lg={3} xl={2} sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1" sx={{ display: 'flex', alignItems: 'center' }}>
              Assigned to
              {isDeptStakeholder && task.defaultAssignee !== defaultSystemTask?.systemTask && (
                <IconCell
                  icon="fa-regular fa-pencil"
                  iconHover="fa-solid fa-pencil"
                  onClick={() => setIsAssignerEdit(true)}
                />
              )}
            </Typography>
            <Box ref={assigneeRef} display="flex" alignItems="center">
              <UserDisplay
                userID={task.assignedUserID}
                user={task.assignee}
                showPicture
                showName
                title={
                  task.assignee?.id === sep?.createdBy
                    ? 'Requestor'
                    : task.department.name
                }
              />
            </Box>
            <UserSearchBar
              type="assigner"
              show={isAssignerEdit}
              anchor={assigneeRef.current}
              onClose={() => setIsAssignerEdit(false)}
              onChange={(user: User) => handleChangeUser('assignee', user)}
            />
          </Grid>
          {/* Owed to */}
          <Grid item xs={12} md={4} lg={3} xl={2} sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1" sx={{ display: 'flex', alignItems: 'center' }}>
              Owed to
              {isDeptStakeholder && (
                <IconCell
                  icon="fa-regular fa-pencil"
                  iconHover="fa-solid fa-pencil"
                  onClick={() => setIsReviewerEdit(true)}
                />
              )}
            </Typography>
            <Box ref={reviewerRef} display="flex" alignItems="center">
              <UserDisplay
                userID={task.defaultReviewerID}
                user={task.defaultReviewer ? task.defaultReviewer : undefined}
                showPicture
                showName
                title={task.department.name}
              />
            </Box>
            <UserSearchBar
              type="reviewer"
              show={isReviewerEdit}
              anchor={reviewerRef.current}
              adAppRoleName={task.department.adAppRole}
              onClose={() => setIsReviewerEdit(false)}
              onChange={(user: User) => handleChangeUser('reviewer', user)}
            />
          </Grid>
          {/* Department */}
          <Grid item xs={12} md={4} lg={2} xl={1.4} sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1">
              Department
            </Typography>
            <Typography fontSize={typographyTokens.fontSizes.caption}>{task.department.name}</Typography>
          </Grid>
          {/* Phase */}
          <Grid item xs={12} md={4} lg={2} xl={1} sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1">
              Phase
            </Typography>
            <Typography fontSize={typographyTokens.fontSizes.caption}>{ReadableTaskPhase[task.phase]}</Typography>
          </Grid>
          {/* Age (if present) */}
          {task.startedAt && (
            <Grid item xs={12} md={4} lg={2} xl={1} sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1">
                Age
              </Typography>
              <Typography fontSize={typographyTokens.fontSizes.caption}>
                <div style={{ background: color ? color : 'transparent', color: ageTextColor, display: "flex", justifyContent: 'flex-start', alignItems: 'center', padding: color ? '0px 3px' : '0px', width: 'fit-content', borderRadius: '4px' }}>
                  <span style={{padding: color ? '1px 4px 1px 4px' : '0px', minWidth: '20px', textAlign: 'center'}}>
                    {activeDays}
                  </span>
                  <span>
                    {userAssigmentDetails && userAssigmentDetails.length > 0 && (
                      <Tooltip
                        style={{ marginLeft: '3px' }}
                        title={
                          <span
                            style={{
                              fontSize: typographyTokens.fontSizes.lg,
                              padding: '16px 8px 12px 8px',
                              maxHeight: '30vh',
                              overflowY: 'auto',
                              display: 'block',
                            }}
                          >
                            <b>User Assignment Details</b><br />
                            {userAssigmentDetails.map((period, index) => (
                              <>
                                <br />
                                <table
                                  key={index}
                                  style={{
                                    marginBottom: '12px',
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                  }}
                                >
                                  <tbody>
                                    <tr>
                                      <td style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>
                                        <b>User:</b> {period.userId}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>
                                        <b>Time:</b> {period.days ?? 'NA'} days, {period.hours ?? 'NA'} hours, {period.mins ?? 'NA'} mins
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ borderBottom: '1px solid #ccc', padding: '4px' }}>
                                        <b>From:</b> {period.from ? period.from.toLocaleString() : 'NA'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ padding: '4px' }}>
                                        <b>To:</b> {period.to ? period.to.toLocaleString() : 'NA'}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </>
                            ))}
                          </span>
                        }
                        placement="top"
                        arrow
                      >
                        <IconButton size="small" style={{ color: ageTextColor ? ageTextColor : theme.palette.gray.darkGray1 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </span>
                </div>
              </Typography>
            </Grid>
          )}
          {/* Escalation Date */}
          <Grid item xs={12} md={4} lg={2} xl={1} sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1">
              Due Date
            </Typography>
            <Typography fontSize={typographyTokens.fontSizes.caption}>
              {getEscalationDate(task, taskTemplates) || 'NA'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TaskHeader;
